// Compact VDB status pill — build state plus, when ready, the live entity count.
// The entity count is the platform's capacity/pricing signal, so this badge is
// reused on Welcome and every system dashboard (Dashboard, Database Management,
// Admin) to keep it always in view. Single source of the label/tone logic.

import { useVdbStatus, type IndexWait, type VdbStatus } from '@/lib/useVdb';
import { useCurrentDatabase } from '@/lib/useDatabases';
import { useLoadGate } from '@/lib/useLoadGate';
import { cn } from '@/lib/utils';

/** At or past 80% of the entitled ceiling — loads are refused at 100%, so the
 *  badge turns amber and says so before the refusal is the first signal. */
function nearCeiling(s: VdbStatus | undefined): boolean {
  return !!s && s.state === 'ready' && !s.waiting && s.entities * 5 >= s.entity_ceiling * 4;
}

/** An index behind the store says what it waits on and whose count it shows:
 *  a cleared store beside "ready · 143,605 entities" reads as a store that did
 *  not clear (UAT 2026-09-28), and "changes waiting" did not say for what
 *  (UAT 2026-10-14). */
function waitingLabel(waiting: IndexWait | undefined, serving: number | undefined): string {
  const when = !waiting
    ? 'rebuilds shortly'
    : waiting.on === 'clear'
      ? 'rebuilds when the clear finishes'
      : waiting.on === 'load'
        ? 'rebuilds when the load finishes'
        : waiting.on === 'settle'
          ? `rebuilds after ${waiting.seconds} s without changes`
          : 'rebuilds shortly';
  const shows = serving === undefined ? '' : ` · shows ${serving.toLocaleString()} until then`;
  return `out of date · ${when}${shows}`;
}

/** "about 14 min" / "about 2 h 10 min" / "under a minute" — a person's estimate. */
function fmtEta(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const m = Math.round(seconds / 60);
  if (m < 60) return `about ${m} min`;
  return `about ${Math.floor(m / 60)} h ${m % 60} min`;
}

function vdbLabel(s: VdbStatus | undefined): string {
  if (!s) return 'checking…';
  switch (s.state) {
    case 'pending':
      return 'pending';
    case 'building': {
      // Queries and search answer from the loaded data throughout; only the
      // knowledge index is catching up — say so first, or "indexing" reads
      // as "not available" (UAT 2026-08-29).
      const ready = s.serving ? 'queries ready · ' : '';
      const p = s.progress;
      if (s.reloading) return 'reloading index…';
      if (p && p.total > 0) {
        const eta = p.eta_s === null ? '' : ` · ${fmtEta(p.eta_s)}`;
        return `${ready}indexing ${p.done.toLocaleString()} of ${p.total.toLocaleString()}${eta}`;
      }
      return s.serving ? `${ready}updating index` : 'building…';
    }
    case 'ready':
      if (s.waiting) return waitingLabel(s.waiting, s.entities);
      return nearCeiling(s)
        ? `ready · ${s.entities.toLocaleString()} of ${s.entity_ceiling.toLocaleString()} entities`
        : `ready · ${s.entities.toLocaleString()} entities`;
    case 'failed':
      return 'failed';
    case 'paused':
      return s.reason === 'out_of_focus'
        ? 'index paused · resumes now'
        : waitingLabel(s.waiting, s.serving_entities);
    case 'released':
      return 'index released · reloading';
    case 'deferred':
      return s.serving_entities === undefined
        ? 'rebuild deferred'
        : `rebuild deferred · serving ${s.serving_entities.toLocaleString()} entities`;
  }
}

function vdbTone(s: VdbStatus | undefined): string {
  if (!s) return 'bg-muted text-tres-text-secondary';
  switch (s.state) {
    case 'pending':
      return 'bg-muted text-tres-text-secondary';
    case 'building':
      return 'bg-tres-status-pending/15 text-tres-status-pending';
    case 'paused':
      return 'bg-tres-status-pending/15 text-tres-status-pending';
    case 'released':
      return 'bg-tres-status-pending/15 text-tres-status-pending';
    case 'ready':
      return nearCeiling(s) || s.waiting
        ? 'bg-tres-status-pending/15 text-tres-status-pending'
        : 'bg-tres-status-active/15 text-tres-status-active';
    case 'failed':
      return 'bg-tres-status-error/15 text-tres-status-error';
    case 'deferred':
      return 'bg-tres-status-error/15 text-tres-status-error';
  }
}

export function VdbBadge({ className }: { className?: string }) {
  const status = useVdbStatus();
  // A running import is at least as important as the index state — the badge
  // is the one always-visible system signal, and a load used to be invisible
  // outside the Data Management page (UAT 2026-09-03).
  const loading = useLoadGate();
  const { database } = useCurrentDatabase();
  const of = database ? ` of the ${database.name} database` : '';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs',
        vdbTone(status.data),
        className,
      )}
      title={
        nearCeiling(status.data)
          ? 'Approaching this site’s entity ceiling — new loads are refused at 100%. Contact your account manager to raise it.'
          : `In-memory index (VDB)${of} — live entity count`
      }
    >
      <span className="font-medium">VDB</span>
      <span>·</span>
      <span>{vdbLabel(status.data)}</span>
      {loading && (
        <>
          <span>·</span>
          <span className="font-medium" title={`Started ${loading.started_at}`}>
            {loading.kind === 'clear'
              ? 'clearing'
              : loading.kind === 'queued-clear'
                ? 'clears queued'
                : 'loading'}
            : {loading.label}
          </span>
        </>
      )}
    </span>
  );
}
